'use client';

/**
 * Button — 링크/액션 겸용 버튼. href 유무로 next/link(<a>)와 <button>을 자동 선택.
 *
 * - theme   : 'solid' | 'text' | 'icon' (기본 solid). icon + filled 면 채워진 아이콘 버튼
 * - type    : 'black' | 'white' — 색상 프리셋. outline이면 채움은 그대로 두고 보더 추가 (black gray-600 / white gray-400)
 * - size    : large~xsmall, [pc, mo] 튜플로 반응형 (기본 large). theme='icon'은 숫자 지정 시 정사각 너비(px)
 * - htmlType: 'button' | 'submit' | 'reset' (기본 button)
 *
 * action(클릭 동작):
 * - leadForm({ key, openType })       : openType 생략 시 single, 'tab'→탭형
 * - leadForm({ keys, openType:'multi' }) : 다중 선택 (keys 생략 시 빈 상태)
 * - popup({ id, content })       : CommonPopup 레이어로 열고 content에 onClose 주입
 * - pdfDownload({ pdf }) : 소개서 다운로드 리드폼 열기 (정적 목록에 없는 지점명은 API 조회)
 * - scrollTo({ id })             : 해당 id 엘리먼트로 부드럽게 스크롤 이동
 *
 * 아이콘: 기본 우측 화살표 자동 부착 / children에 svg·Icon 있으면 생략 / icon 으로 커스텀, false 로 숨김
 * 컬러: bg·text·hover·active → resolveColor → --btn-* CSS 변수 / borderColor 는 인라인 border-color (지정 시 1px solid 자동)
 * 기타: outline, radius='max', width, ButtonGroup(vertical·align) 정렬
 * callout: 버튼 위 가운데 말풍선(EventBubble). 문자열 또는 EventBubble props. 기본 textSize 12 + float
 *          위치가 다르면 callout.className 으로 덮는다
 *
 * @example
 * <Button href="/a.pdf" download>다운로드</Button>
 * <Button action={{ key: 'estimate' }}>견적 문의</Button>
 * <Button action={{ type: 'pdfDownload', pdf: '공유오피스' }}>소개서 다운로드</Button>
 * <Button action={{ type: 'pdfDownload', pdf: '송도센트럴점' }}>소개서 다운로드</Button>
 * <Button href="/a" callout="최저가 확인!">무료 견적 받기</Button>
 * <Button href="/a" callout={{ text: '최저가 확인!', bgColor: 'color-blue-600' }}>무료 견적 받기</Button>
 */
import clsx from 'clsx';
import Link from 'next/link';
import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  Children,
  cloneElement,
  type CSSProperties,
  forwardRef,
  isValidElement,
  type MouseEventHandler,
  type ReactElement,
  type ReactNode,
  type Ref,
  useContext,
} from 'react';
import {
  CommonPopupContext,
  useIsInPopup,
} from '@/publishing/components/popup/CommonPopupContext';
import { resolveColor } from '@publishing/helpers/format';
import {
  resolveResponsiveValue,
  type ResponsiveValue,
} from '@publishing/helpers/responsive';
import {
  type FormKey,
  type FormActionFormName,
  resolveFormActionFormName,
} from '@publishing/modules/forms';
import FormLayerPopup from '@publishing/modules/forms/FormLayerPopup';
import {
  PdfDownloadPopup,
  usePdfBranchList,
} from '@publishing/modules/forms/pdf/PdfDownloadPopup';
import type { PdfDownloadName } from '@publishing/modules/forms/pdf/data';
import {
  buildFormProps,
  resolveStaticPdfDownload,
} from '@publishing/modules/forms/pdf/utils';
import {
  openPopup,
  openPopupMulti,
  openPopupTab,
} from '@publishing/modules/forms/FormPopup';
import {
  FORM_MENUS,
  POPUP_FORM_NAME,
  POPUP_LEAD_TYPE,
} from '@publishing/modules/forms/_config';
import {
  leadFormOpenAttributes,
  pushLeadFormOpen,
  type ItemCategory,
} from '@/features/lead-form/utils/leadFormGtm';
import type { LeadFormName } from '@/features/lead-form/constants/formName';
import { GTM_EVENT, GTM_MANUAL_PUSH_ATTR } from '@/shared/constants/gtm';
import { gtmClick } from '@/shared/utils/gtm';
import EventBubble, { type EventBubbleProps } from '@publishing/components/events/EventBubble';
import type { ProductType } from '@/features/lead-form/types/leadData';
import Icon from './Icon';
import { ArrowArrow } from './SvgSprite';
import s from './Button.module.scss';

// ============================================
// Button props — 컴포넌트 동작/스타일 직접 제어
// ============================================
export type BaseButtonProps = {
  type?: 'black' | 'white';
  htmlType?: 'button' | 'submit' | 'reset';
  theme?: 'solid' | 'text' | 'icon';
  /** theme='icon' 전용 — 배경 채운 아이콘 버튼. 색은 type 프리셋을 따르고, bgColor 로 덮을 수 있다 */
  filled?: boolean;
  /** large~xsmall 프리셋 또는 숫자(px) — 숫자는 theme='icon'에서 정사각 너비로 적용 */
  size?: ResponsiveValue<ButtonSizeValue>;
  outline?: boolean;
  radius?: 'max';
  width?: string;
  bgColor?: string;
  textColor?: string;
  hoverColor?: string;
  activeColor?: string;
  borderColor?: string;
  className?: string;
  children?: ReactNode;
  icon?: ButtonIconProp;
  action?: ButtonAction;
  callout?: string | EventBubbleProps;
};

type ButtonLinkProps = {
  href?: string;
  target?: '_blank' | '_self';
  download?: boolean | string;
  onClick?:
    | MouseEventHandler<HTMLButtonElement>
    | MouseEventHandler<HTMLAnchorElement>;
};

type ButtonOwnProps = BaseButtonProps &
  ButtonLinkProps &
  Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    keyof BaseButtonProps | keyof ButtonLinkProps | 'type' | 'onClick'
  >;

export type ButtonProps = ButtonOwnProps;

// ============================================
// props 구성 요소 — size / action / icon
// ============================================
type ButtonSize = 'large' | 'medium' | 'small' | 'xsmall';
type ButtonSizeValue = ButtonSize | number;

// 버튼 size별 기본 화살표 아이콘 px
const ICON_SIZE_BY_BUTTON_SIZE: Record<ButtonSize, number> = {
  large: 20,
  medium: 16,
  small: 16,
  xsmall: 12,
};

/**
 * 오픈 이벤트(`click_leadform`) GA 파라미터만 덮어쓴다 — 팝업 동작·제출 formName 과 무관.
 *
 * 기본값(`N_상품문의` + `공유오피스` + 선택 상품)과 태그 스펙이 다른 CTA 에만 지정한다.
 * `[]` 는 명시적 공란(`item_category=""`) — 키를 빼면 GTM 데이터 영역에 남은
 * 직전 이벤트 값이 그대로 실린다.
 */
type LeadFormGtmOpen = {
  formName?: LeadFormName;
  /** 한 페이지에 리드폼이 여럿일 때의 구분값(1-based) — 페이지가 직접 부여한다. */
  leadFormNum?: number;
  leadType?: readonly ItemCategory[];
  productType?: readonly ProductType[];
};

type LeadFormSingleTabAction = {
  type?: 'leadForm';
  openType?: 'single' | 'tab';
  key: FormKey;
  keys?: never;
  /** LEAD_FORM_NAME.PROMOTION 또는 'PROMOTION' — single 시 submit formName */
  formName?: FormActionFormName;
  gtmOpen?: LeadFormGtmOpen;
};

type LeadFormMultiAction = {
  type?: 'leadForm';
  openType: 'multi';
  keys?: FormKey[];
  /** keys 미지정 시 1개만 미리 선택할 때 */
  key?: FormKey;
  formName?: FormActionFormName;
  gtmOpen?: LeadFormGtmOpen;
};

export type ButtonAction =
  | LeadFormSingleTabAction
  | LeadFormMultiAction
  | { type: 'pdfDownload'; pdf: PdfDownloadName }
  | {
      type: 'popup';
      id: string;
      /** 지정 시 FormPopup과 동일한 폼 셸(헤더·닫기)로 content를 감쌉니다 */
      title?: string;
      content: ReactElement<{ onClose?: () => void }>;
      /** GA btnname — 미지정 시 팝업 id. 버튼 문구가 id 와 다를 때만 지정. */
      gtmBtnName?: string;
    }
  | {
    type: 'scrollTo';
    id: string;
  };

export type ButtonIconConfig = {
  node: ReactNode;
  position?: 'left' | 'right';
};

export type ButtonIconProp = ButtonIconConfig | false;

// ============================================
// 내부 구현용 — 스타일 / 하위 컴포넌트
// ============================================
type ButtonStyle = CSSProperties & {
  '--btn-bg'?: string;
  '--btn-color'?: string;
  '--btn-hover'?: string;
  '--btn-active'?: string;
  '--btn-size'?: string;
  '--btn-mobile-size'?: string;
};

/** `action` 이 붙인 GTM 마크업 속성 — 값은 자유 문자열이라 인덱스 시그니처로 받는다. */
type ButtonActionProps = ButtonLinkProps & Record<`data-${string}`, string | undefined>;

type ButtonBaseProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  keyof BaseButtonProps | 'type' | 'onClick' | 'style'
> &
  ButtonActionProps & {
    htmlType?: BaseButtonProps['htmlType'];
    buttonClassName: string;
    buttonStyle: ButtonStyle;
    buttonChildren: ReactNode;
  };

interface ButtonGroupProps {
  children: ReactNode;
  vertical?: boolean;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

// ============================================
// 외부 전용 — 호출부에서 버튼 데이터를 선언할 때 쓰는 형태
// ============================================
export type ButtonConfig = ButtonLinkProps &
  Pick<
    BaseButtonProps,
    'type' | 'outline' | 'size' | 'width' | 'className' | 'borderColor'
  > & {
    text: string;
    icon?: ButtonIconProp;
    action?: ButtonAction;
  };

function resolveButtonIcon({
  children,
  icon,
  iconSize,
}: {
  children?: ReactNode;
  icon?: ButtonIconProp;
  iconSize: ResponsiveValue<number>;
}): ButtonIconConfig | undefined {
  if (icon === false) return undefined; // 아이콘 숨김

  // children 으로 직접 넘어온 svg/Icon 만 아이콘으로 본다
  // (중첩까지 훑으면 EventBubble 팁 svg 같은 장식 요소에도 걸린다)
  const hasIcon = Children.toArray(children).some(
    (child) => isValidElement(child) && (child.type === 'svg' || child.type === Icon),
  );
  if (hasIcon) return undefined;

  if (icon) return icon; // 커스텀 아이콘
  return {
    node: <Icon name={ArrowArrow} direction="right" size={iconSize} />,
    position: 'right',
  }; // 기본 우측 화살표 — 버튼 size에 맞춘 px
}

function isButtonSizePreset(size: ButtonSizeValue): size is ButtonSize {
  return typeof size === 'string';
}

function getButtonIconSize(size: ButtonSizeValue): number {
  if (isButtonSizePreset(size)) return ICON_SIZE_BY_BUTTON_SIZE[size];
  return size;
}

function getButtonSizeClass(size: ButtonSizeValue, mobile = false): string {
  if (isButtonSizePreset(size)) {
    return s[`btn_${mobile ? 'mobile_' : ''}${size}`];
  }

  return mobile ? s.btn_mobile_custom_size : s.btn_custom_size;
}

// 기능 담당: action 선언을 폼, 팝업 동작으로 변환합니다.
function useButtonAction(
  action?: ButtonAction,
  btnLabel?: string,
): ButtonActionProps {
  const commonPopup = useContext(CommonPopupContext);
  usePdfBranchList(action?.type === 'pdfDownload' ? action.pdf : undefined);

  if (!action) return {};

  switch (action.type) {
    case undefined:
    case 'leadForm': {
      // 시트 row 36 — 리드폼을 여는 CTA 는 페이지 곳곳에 흩어져 있지만
      // 전부 이 action 을 거치므로 여기 한 곳에서 파라미터를 만든다.
      const selectedKeys =
        action.openType === 'multi'
          ? (action.keys ?? (action.key != null ? [action.key] : []))
          : action.key != null
            ? [action.key]
            : [];
      const resolvedProductType = selectedKeys
        .map(
          (key) => FORM_MENUS.find((menu) => menu.key === key)?.productTypeValue,
        )
        .filter((v): v is ProductType => !!v);

      const gtmParams = {
        btnName: btnLabel ?? '리드폼 열기',
        formName:
          action.gtmOpen?.formName ??
          (action.formName
            ? resolveFormActionFormName(action.formName)
            : POPUP_FORM_NAME),
        leadFormNum: action.gtmOpen?.leadFormNum,
        leadType: action.gtmOpen?.leadType ?? POPUP_LEAD_TYPE,
        // 메뉴 key 가 곧 상품이라 오픈 시점에도 item_category2 를 특정할 수 있다.
        // 특정할 수 없으면 undefined — 오버라이드가 없는 한 키 자체를 내보내지 않는다.
        productType:
          action.gtmOpen?.productType ??
          (resolvedProductType.length ? resolvedProductType : undefined),
      };

      return {
        /**
         * 마크업에도 스펙대로 data-* 를 남긴다 (태그 QA·GTM 요소 확인용).
         * 팝업을 여는 시점은 클릭과 별개로 처리해야 해 집계는 아래 onClick 한 곳에서만
         * 하며, 전역 위임 리스너가 중복 push 하지 않도록 수동 표시를 함께 붙인다.
         */
        ...leadFormOpenAttributes(gtmParams),
        [GTM_MANUAL_PUSH_ATTR]: '',
        onClick: () => {
          // 오픈 이벤트의 리드폼 구분값을 팝업 제출까지 잇는다.
          const openOptions =
            action.formName != null || action.gtmOpen?.leadFormNum != null
              ? {
                  ...(action.formName != null && { formName: action.formName }),
                  ...(action.gtmOpen?.leadFormNum != null && {
                    leadFormNum: action.gtmOpen.leadFormNum,
                  }),
                }
              : undefined;

          pushLeadFormOpen(gtmParams);

          if (action.openType === 'tab') {
            openPopupTab(action.key, openOptions);
            return;
          }

          if (action.openType === 'multi') {
            const keys =
              action.keys ?? (action.key != null ? [action.key] : undefined);
            openPopupMulti(keys, openOptions);
            return;
          }

          openPopup(action.key, openOptions);
        },
      };
    }

    case 'pdfDownload': {
      // 오픈 이벤트의 leadform_name·item_category 를 팝업 제출(click_submit)과 맞춘다.
      const staticDownload = resolveStaticPdfDownload(action.pdf);
      const { formName, leadType, productType } = buildFormProps({
        category: staticDownload ? 'product' : 'branch',
        productName: staticDownload?.name,
        downloadPurpose: [],
      });
      const gtmParams = {
        btnName: btnLabel ?? `${action.pdf} 소개서 다운로드`,
        formName,
        leadType,
        productType,
      };

      return {
        ...leadFormOpenAttributes(gtmParams),
        [GTM_MANUAL_PUSH_ATTR]: '',
        onClick: () => {
          if (!commonPopup) return;
          pushLeadFormOpen(gtmParams);
          commonPopup.openLayer(
            `pdf-download-${action.pdf}`,
            (close) => (
              <PdfDownloadPopup
                pdf={action.pdf}
                btnName={gtmParams.btnName}
                onClose={close}
              />
            ),
            undefined,
            false,
          );
        },
      };
    }

    case 'popup':
      return {
        onClick: () => {
          if (!commonPopup) {
            if (process.env.NODE_ENV !== 'production') {
              console.warn('Button popup requires CommonPopupProvider.');
            }
            return;
          }

          commonPopup.openLayer(
            action.id,
            (close) => {
              const content = cloneElement(action.content, { onClose: close });
              if (!action.title) return content;
              return (
                <FormLayerPopup title={action.title} onClose={close}>
                  {content}
                </FormLayerPopup>
              );
            },
            undefined,
            action.gtmBtnName,
          );
        },
      };

    case 'scrollTo':
      return {
        // 시트 row 48 click_navigation — 같은 페이지 안 특정 위치로 이동하는 CTA.
        // 클릭이 곧 트리거라 집계는 전역 위임 리스너에 맡기고 마크업에 data-* 만 남긴다.
        ...gtmClick(GTM_EVENT.NAVIGATION, btnLabel ?? action.id),
        onClick: () => {
          document
            .getElementById(action.id)
            ?.scrollIntoView({ behavior: 'smooth' });
        },
      };
  }

  return {};
}

// 호출부: 외부에서는 Button 하나만 사용하고, href/action과 스타일 props만 넘깁니다.
export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(function Button(props, ref) {
  const {
    theme = 'solid',
    filled,
    type = 'black',
    htmlType = 'button',
    size = 'large',
    outline,
    width,
    radius,
    bgColor,
    textColor,
    borderColor,
    hoverColor,
    activeColor,
    className,
    children,
    icon,
    disabled,
    style,
    action,
    callout,
    ...rest
  } = props;

  /**
   * children 이 아이콘을 동반하면 문자열이 아니라 라벨을 뽑을 수 없다.
   * 그 경우 `aria-label` 을 GA 라벨로 쓴다 — 이동 버튼(`navigationGtm`)과 동일한 fallback.
   */
  const btnLabel =
    (typeof children === 'string' ? children : undefined) ??
    (rest as { 'aria-label'?: string })['aria-label'];
  const actionProps = useButtonAction(action, btnLabel);
  const isInPopup = useIsInPopup();

  /**
   * 시트 row 27 `click_btn` / row 29 `click_popup` 기본 부여.
   *
   * 이동하는 버튼(`href`)만 대상이며, 팝업 안이면 29 로 자동 분기한다.
   * 호출부가 `gtmClick` 등으로 이미 `data-event` 를 넘겼으면 그 명시 태깅이 우선한다
   * (배너 row 28, 다운로드 row 35 등). `action` 방식은 자체 집계라 제외.
   */
  const linkRest = rest as { href?: string; 'data-event'?: string; 'aria-label'?: string };
  const navigationGtm =
    linkRest.href && !linkRest['data-event'] && !action
      ? gtmClick(
          isInPopup ? GTM_EVENT.POPUP : GTM_EVENT.BTN,
          btnLabel ?? linkRest['aria-label'] ?? linkRest.href,
          linkRest.href,
        )
      : undefined;
  const isIconFilled = theme === 'icon' && !!filled;
  const [pcSize = 'large', moSize] = resolveResponsiveValue(size);
  const iconSize: ResponsiveValue<number> = moSize
    ? [getButtonIconSize(pcSize), getButtonIconSize(moSize)]
    : getButtonIconSize(pcSize);
  const buttonIcon = resolveButtonIcon({ children, icon, iconSize });

  const buttonStyle = {
    '--btn-bg': bgColor ? resolveColor(bgColor) : undefined,
    '--btn-color': textColor ? resolveColor(textColor) : undefined,
    borderColor: borderColor ? resolveColor(borderColor) : undefined,
    '--btn-hover': hoverColor ? resolveColor(hoverColor) : undefined,
    '--btn-active': activeColor ? resolveColor(activeColor) : undefined,
    width,
    borderStyle: borderColor ? 'solid' : undefined,
    borderWidth: borderColor ? '1px' : undefined,
    borderRadius: radius ? `var(--radius-${radius})` : undefined,
    '--btn-size': typeof pcSize === 'number' ? `${pcSize}px` : undefined,
    '--btn-mobile-size': typeof moSize === 'number' ? `${moSize}px` : undefined,
    ...style,
  } as ButtonStyle;
  const calloutProps = typeof callout === 'string' ? { text: callout } : callout;
  const buttonChildren = (
    <>
      {buttonIcon?.position === 'left' && buttonIcon.node}
      {children}
      {(!buttonIcon?.position || buttonIcon.position === 'right') &&
        buttonIcon?.node}
      {calloutProps && (
        <EventBubble
          textSize={12}
          float
          {...calloutProps}
          className={clsx(s.btn_callout, calloutProps.className)}
        />
      )}
    </>
  );

  return (
    <ButtonBase
      ref={ref}
      {...navigationGtm}
      {...rest}
      {...actionProps}
      htmlType={htmlType}
      disabled={disabled}
      buttonClassName={`${s.btn} ${s[`btn_${theme}`]} ${isIconFilled ? s.btn_icon_filled : ''} ${s[`btn_${type}`]} ${getButtonSizeClass(pcSize)} ${moSize ? getButtonSizeClass(moSize, true) : ''} ${outline ? s.btn_outline : ''} ${disabled ? s.btn_disabled : ''} ${callout ? s.btn_has_callout : ''} ${className ?? ''}`}
      buttonStyle={buttonStyle}
      buttonChildren={buttonChildren}
    />
  );
});

// 태그 렌더 담당: href 유무에 따라 Link 또는 button만 선택합니다.
const ButtonBase = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonBaseProps
>(function ButtonBase(
  {
    htmlType = 'button',
    disabled,
    href,
    target,
    download,
    onClick,
    tabIndex,
    buttonClassName,
    buttonStyle,
    buttonChildren,
    ...rest
  },
  ref,
) {
  if (href) {
    const handleClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
      if (disabled) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      (onClick as MouseEventHandler<HTMLAnchorElement> | undefined)?.(event);
    };

    return (
      <Link
        ref={ref as Ref<HTMLAnchorElement>}
        href={href}
        target={target}
        download={download}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        {...(rest as Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>)}
        onClick={disabled || onClick ? handleClick : undefined}
        className={buttonClassName}
        style={buttonStyle}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : tabIndex}
      >
        {buttonChildren}
      </Link>
    );
  }

  return (
    <button
      ref={ref as Ref<HTMLButtonElement>}
      type={htmlType}
      disabled={disabled}
      onClick={onClick as MouseEventHandler<HTMLButtonElement> | undefined}
      tabIndex={tabIndex}
      {...rest}
      className={buttonClassName}
      style={buttonStyle}
    >
      {buttonChildren}
    </button>
  );
});

// 묶음 레이아웃 담당: 버튼 간 방향과 정렬만 제어합니다.
export function ButtonGroup({
  children,
  vertical,
  align = 'left',
  className,
}: ButtonGroupProps) {
  return (
    <div
      className={clsx(s.btn_group, vertical && s.btn_group_vertical, s[`btn_group_${align}`], className)}
    >
      {children}
    </div>
  );
}
